import { useState } from "react";
import axios from "axios";
import { apiUrl } from "features/configure";
import { Box } from "@mui/material";
import { get, startCase } from "lodash-es";
import { useOutletContext } from "react-router";
import { useIntl } from "react-intl";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import { IconButton } from "@metacrm/metacrm-material-ui/dist/Button";
import { useTheme } from "@mui/material";
import { useDispatch } from "react-redux";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import { enqueueSnackbar } from "features/common/redux/actions";
import { formatNumberWithSymbol } from "features/helpers/utils";

export default function useGetKeywordTable() {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const theme = useTheme();
  const [entityName] = useOutletContext();

  const [anchorEl, setAnchorEl] = useState(null);
  const [refetchData, setRefetchData] = useState(false);
  const [actionsList, setActionsList] = useState([]);

  const headers = [
    {
      title: "Keyword",
      label: formatMessage({ id: "global.header.keyword" }),
      disabledSort: true,
      show: true,
      id: "keyword",
      width: 270,
    },
    {
      title: "Mention Count",
      label: (
        <Box display="flex" alignItems="center" gap="5px">
          {formatMessage({ id: "global.header.mentionCount" })}{" "}
          <IconTooltip
            content={formatMessage({
              id: "global.tooltip.mentionCount",
            })}
          />
        </Box>
      ),
      disabledSort: false,
      show: true,
      id: "mentionCount",
      width: 130,
    },
    {
      title: "Impression",
      label: (
        <Box display="flex" alignItems="center" gap="5px">
          {formatMessage({ id: "global.header.impression" })}{" "}
          <IconTooltip
            content={formatMessage({
              id: "global.tooltip.impression",
            })}
          />
        </Box>
      ),
      disabledSort: true,
      show: true,
      id: "impression",
    },
    {
      title: "Engagement",
      label: (
        <Box display="flex" alignItems="center" gap="5px">
          {formatMessage({ id: "global.header.engagement" })}{" "}
          <IconTooltip
            content={formatMessage({
              id: "global.tooltip.engagement",
            })}
          />
        </Box>
      ),
      disabledSort: false,
      show: true,
      id: "engagement",
    },
    {
      title: "Actions",
      label: formatMessage({ id: "global.header.actions" }),
      disabledSort: false,
      show: true,
      id: "actions",
    },
  ];

  const handleDelete = async (keywordId) => {
    try {
      const { data } = await axios.post(
        `${apiUrl}/api/kol/${entityName}/keyword/${keywordId}/delete`
      );
      setRefetchData(true);
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: get(error, "response.data.error", "error"),
        })
      );
    }
  };
  const handleDeleteConfirm = (keywordId) => {
    confirm({
      title: formatMessage({
        id: "keyword.deleteKeyword.title",
      }),
      content: formatMessage({
        id: "keyword.deleteKeyword.description",
      }),
      confirmText: formatMessage({
        id: "popup.confirmToDelete",
      }),
      cancelText: formatMessage({
        id: "popup.confirmCancel",
      }),
    })
      .then(async () => {
        await handleDelete(keywordId);
      })
      .catch((err) => {
        console.log("cancel", err);
      });
  };

  const commonActions = (keyword) => [
    {
      children: (
        <Box color={theme.customColors.pink[600]}>
          {formatMessage({ id: "global.action.deleteKeyword" })}
        </Box>
      ),
      func: (e) => handleDeleteConfirm(keyword._id),
    },
  ];
  const handleClickActions = (event, keyword) => {
    event.stopPropagation();

    setActionsList([...commonActions(keyword)]);

    setAnchorEl(event.currentTarget);
  };
  const handleCloseActions = () => {
    setAnchorEl(null);
  };

  const renderContents = (keyword) => {
    let contents = [
      <div slot="keyword">{keyword?.keyword} </div>,
      <div slot="mentionCount">
        {formatNumberWithSymbol(keyword?.mentionCount) || "-"}
      </div>,
      <div slot="impression">{keyword?.impression || "-"}%</div>,
      <div slot="engagement">{keyword?.engagement || "-"}%</div>,
      <div slot="actions">
        <IconButton
          onClick={(e) => handleClickActions(e, keyword)}
          size={24}
          color={theme.customColors.purple[500]}
        >
          <i className="meta-crm-icon-ic_dots font-size-24" />
        </IconButton>
      </div>,
    ];
    return contents;
  };

  return {
    headers,
    renderContents,
    anchorEl,
    actionsList,
    handleCloseActions,
    refetchData,
  };
}
